import { redirect } from '@tanstack/react-router';
import { decodeIdentity } from './identity';
import { canOpen, type AdminRoute } from './roles';
import { ensureAccess } from './token-store';

/**
 * Route-level permission guards (M19, audit finding O-06).
 *
 * `/users`, `/audit` and `/config` opened by direct URL for an operator whose nav does not show
 * them: the shell rendered, then the API refused the data. **Server enforcement was and is
 * intact** — this is a UI correctness bug, not an authorization hole — but rendering a page
 * someone cannot use is a bug on its own terms.
 *
 * The permission comes from `ROUTE_PERMISSIONS`, which `AppShell`'s nav reads too — so a route
 * cannot end up reachable by URL but hidden in the nav, or the reverse. That relationship is
 * structural rather than merely tested: there is one map, and both consumers read it.
 *
 * **Every route under `authedRoute` carries this guard, including the ones the map leaves
 * open.** Omitting it where the answer is currently `null` would make the map advisory: giving
 * one of those routes a permission later would change the nav and quietly not change what a
 * typed URL reaches. `/forbidden` is guarded too and is safe from looping because its entry is
 * `null` — asserted in `roles.test.ts` rather than left as a comment.
 */
export function requireRoute(route: AdminRoute) {
  return async () => {
    // **Await the token first.** On a hard reload the access token is null until `ensureAccess`
    // resolves (`auth.tsx` hydrates it in an effect), so a synchronous guard would decode
    // `roles: []` and bounce a legitimate operator off their own bookmark. After the first load
    // it is cached, so this costs nothing.
    const token = await ensureAccess();

    // A null token is not a permission answer. `refreshAccess` deliberately returns null while
    // *keeping* the session on a transport error, a 429 or a 5xx — so treating that as "no
    // roles" would strand an authorized operator on /forbidden, where nothing retries once the
    // service recovers. While the session still exists, let the route render: its own queries
    // surface and retry the failure, and the server authorizes every read regardless.
    // A session that is genuinely gone is `authedRoute`'s business, not this guard's.
    if (!token) return;

    if (!canOpen(decodeIdentity().roles, route)) throw redirect({ to: '/forbidden' });
  };
}
